/**
 * The themes' colours, fonts and corner rounding, from the database (owner, 2026-10-17: "the theming must be database
 * first, not held in config files"; board D61).
 *
 * GET /api/themes sends every theme; this turns them into CSS, one block per theme class on <html>
 * (`html.grow { --card: 0 0% 100%; ... }`), and puts it on the page in one <style> element. Components keep naming
 * colours as before (bg-card, text-muted-foreground): Tailwind maps each name to its CSS variable, and the variable's
 * value is now the database's.
 *
 * The CSS is kept on the device with the version it came from, and index.html's first-paint script puts it on the
 * page before anything draws, so the app opens in the right colours; the request then asks only whether the version
 * changed (304 when not). style.css keeps one fallback, the default theme's colours on :root, for a first-ever open
 * with no connection.
 *
 * A theme whose fonts are "system" sets no font rule: the phone's own font, as Tailwind's base styles give it. A
 * theme whose title face differs from its reading face sets it on h1-h3, as style.css did for Grow and Field notes,
 * at the lowest weight a rule can have (:where), so a heading that asks for another face (font-body for small
 * capital labels, font-grow for the wordmark) gets it. Each theme's font stylesheet (its link) is put on the page
 * when it is not there already, so a theme can bring a font the app has never loaded.
 *
 * The kept copy carries the format it was made in (FORMAT): a change to how the CSS is written makes the next open
 * write it again, though the themes' version is the same.
 *
 * Everything from the server is checked before it becomes CSS (names, values, font stacks), so nothing in a row can
 * break out of its declaration.
 *
 * The same first-paint plumbing also keeps the Grow settings answer (lib/growSettings; architecture audit A64,
 * batch 11a): readKeptSettings and keepSettings hand the last answer's text to and from the device, so the app opens
 * with the owner's settings it last heard, and a request that fails leaves that copy standing. It is public
 * settings the server sent, not the gardener's data, and it goes through this module because this is the file
 * check-storage allows for the first paint.
 *
 * Who calls it: main.tsx (loadThemeTokens, once at start); lib/growSettings (readKeptSettings, keepSettings).
 *
 * @module theme/themeTokens
 */

import { apiClient } from '@/shared/api/apiClient';
import { THEME_TOKENS_STORAGE_KEY } from '@/config/tunables/shared';

/** A theme as GET /api/themes sends it. */
export interface AppTheme {
  /** The class the app puts on <html> (grow, fieldnotes, light, dark). */
  slug: string;
  /** Its name, as the profile lists it. */
  name: string;
  /** The theme a gardener with no choice gets. */
  default: boolean;
  /** The app's dark: classes apply too. */
  dark: boolean;
  /** Font stacks for reading, titles and notes ("system" for the phone's own), the notes' style, and the stylesheet that loads them. */
  fonts: { body: string; display: string; note: string; noteStyle: string; link: string | null };
  /** Corner rounding, a CSS length. */
  radius: string;
  /** Every colour by name, an HSL triple ("h s% l%") each. */
  colours: Record<string, string>;
}

/** What GET /api/themes answers when it has the themes. */
interface ThemesResponse {
  success: boolean;
  version: string;
  themes: AppTheme[];
}

/** What is kept on the device: the CSS, the version it came from, the format it was written in, and which themes are dark. */
interface StoredThemes {
  version: string;
  css: string;
  dark: string[];
  format?: number;
  /** The themes' font stylesheets, put on the page with the kept CSS. */
  links?: string[];
}

/** How the CSS is written; raised whenever cssForThemes changes what it writes, so kept copies are written again. */
const FORMAT = 2;

/** A font stylesheet the app may load: Google Fonts' only. */
const FONT_LINK = /^https:\/\/fonts\.googleapis\.com\/css2\?[A-Za-z0-9=:;@,+&%._-]+$/;

/** localStorage: the Grow settings answer as the server last sent it, kept as JSON text for the next first paint (lib/growSettings). */
const GROW_SETTINGS_KEY = 'grow.settings.v1';

/** The <style> element the themes are painted with; index.html's first-paint script makes it under the same id. */
const STYLE_ID = 'grow-theme-tokens';

/** A theme's class name: lowercase letters, digits and hyphens, starting with a letter. */
const SLUG = /^[a-z][a-z0-9-]{0,23}$/;
/** A colour variable's name, without the leading hyphens. */
const COLOUR_NAME = /^[a-z][a-z0-9-]{0,47}$/;
/** A colour as an HSL triple ("h s% l%"). */
const TRIPLE = /^\d{1,3}(\.\d+)?\s+\d{1,3}(\.\d+)?%\s+\d{1,3}(\.\d+)?%$/;
/** A CSS length in rem, px or em (the corner rounding). */
const LENGTH = /^\d+(\.\d+)?(rem|px|em)$/;
/** A font stack: names, quotes, commas and spaces only; nothing that could end a declaration. */
const FONT_STACK = /^[A-Za-z0-9 ,"'-]{1,255}$/;

/**
 * A font stack fit for a declaration, or null for the phone's own font (or anything that does not read as a stack).
 *
 * @param stack - As the server sent it.
 * @returns The stack, or null.
 */
function fontOf(stack: string | null | undefined): string | null {
  const value = String(stack ?? '').trim();
  if (!value || value === 'system' || !FONT_STACK.test(value)) return null;
  return value;
}

/**
 * The CSS for these themes: each one's colours, rounding and font variables on `html.<slug>`, then its reading face
 * on the page and its title face on h1-h3 where it has its own.
 *
 * @param themes - As GET /api/themes sent them.
 * @returns The CSS text.
 */
export function cssForThemes(themes: AppTheme[]): string {
  const blocks: string[] = [];
  for (const theme of themes) {
    if (!SLUG.test(theme.slug)) continue;
    const vars: string[] = [];
    for (const [name, value] of Object.entries(theme.colours ?? {})) {
      if (COLOUR_NAME.test(name) && TRIPLE.test(String(value).trim())) vars.push(`--${name}:${String(value).trim()}`);
    }
    if (LENGTH.test(String(theme.radius ?? ''))) vars.push(`--radius:${theme.radius}`);
    const body = fontOf(theme.fonts?.body);
    const display = fontOf(theme.fonts?.display);
    const note = fontOf(theme.fonts?.note);
    if (body) vars.push(`--font-body:${body}`);
    if (display) vars.push(`--font-display:${display}`);
    if (note) vars.push(`--font-note:${note}`);
    vars.push(`--font-note-style:${theme.fonts?.noteStyle === 'italic' ? 'italic' : 'normal'}`);
    blocks.push(`html.${theme.slug}{${vars.join(';')}}`);
    // The same selectors style.css used for Grow and Field notes, so which rule wins over a font class is unchanged.
    if (body) blocks.push(`.${theme.slug} body,.${theme.slug}{font-family:${body}}`);
    if (display && display !== body) blocks.push(`:where(.${theme.slug}) h1,:where(.${theme.slug}) h2,:where(.${theme.slug}) h3{font-family:${display}}`);
  }
  return blocks.join('\n');
}

/**
 * Puts each theme's font stylesheet on the page, once: a link already there (index.html's, or one put earlier) is
 * not added again.
 *
 * @param links - The themes' font stylesheets (anything that is not a Google Fonts stylesheet is left out).
 */
export function loadThemeFonts(links: Array<string | null | undefined>): void {
  const have = new Set([...document.querySelectorAll('link[rel="stylesheet"]')].map((l) => (l as HTMLLinkElement).href));
  for (const link of new Set(links.filter((l): l is string => Boolean(l) && FONT_LINK.test(String(l))))) {
    if (have.has(link)) continue;
    const el = document.createElement('link');
    el.rel = 'stylesheet';
    el.href = link;
    el.dataset.themeFonts = '';
    document.head.appendChild(el);
    have.add(link);
  }
}

/**
 * Puts this CSS on the page, in the one <style> element the themes use (made at the end of <head> if missing).
 *
 * @param css - From cssForThemes.
 */
export function paintThemes(css: string): void {
  let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement('style');
    style.id = STYLE_ID;
    document.head.appendChild(style);
  }
  if (style.textContent !== css) style.textContent = css;
}

/**
 * The themes kept on this device, or null when there are none or storage is refused.
 *
 * @returns What was kept.
 */
function readStored(): StoredThemes | null {
  try {
    const raw = localStorage.getItem(THEME_TOKENS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredThemes;
    return typeof parsed?.css === 'string' && typeof parsed?.version === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Keeps these themes on the device for the next first paint. Storage refused: they are on the page, just not kept.
 *
 * @param stored - The CSS, its version and the dark themes.
 */
function keep(stored: StoredThemes): void {
  try {
    localStorage.setItem(THEME_TOKENS_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Storage refused: the next open paints from the stylesheet's fallback until the server answers.
  }
}

/**
 * The Grow settings answer kept on this device, as the JSON text it was kept as.
 *
 * @returns The text, or null when nothing was kept or storage is refused.
 */
export function readKeptSettings(): string | null {
  try {
    return localStorage.getItem(GROW_SETTINGS_KEY);
  } catch {
    return null;
  }
}

/**
 * Keeps the Grow settings answer on the device for the next first paint. Storage refused: it is in use, just not kept.
 *
 * @param text - The answer as JSON text.
 */
export function keepSettings(text: string): void {
  try {
    localStorage.setItem(GROW_SETTINGS_KEY, text);
  } catch {
    // Storage refused: the next open waits for the server's answer.
  }
}

/**
 * Paints the kept themes at once, then asks the server whether they changed and paints the new ones if so. Before
 * the theme tables are made (503), or with no connection, the page keeps what it has.
 *
 * @returns Resolves when done; never rejects.
 */
export async function loadThemeTokens(): Promise<void> {
  const stored = readStored();
  if (stored) {
    paintThemes(stored.css);
    loadThemeFonts(stored.links ?? []);
  }
  // A copy written in an older format is asked for whole, not only whether it changed.
  const current = stored && stored.format === FORMAT ? stored : null;
  try {
    const response = await apiClient.get<ThemesResponse>('/themes', {
      headers: current ? { 'If-None-Match': `"${current.version}"` } : undefined,
      // 304 (nothing changed) and 503 (the tables are not made yet) are answers, not failures.
      validateStatus: (status) => status === 200 || status === 304 || status === 503,
    });
    if (response.status !== 200 || !response.data?.success || !Array.isArray(response.data.themes)) return;
    const { version, themes } = response.data;
    const links = themes.map((t) => t.fonts?.link ?? null).filter((l): l is string => Boolean(l));
    loadThemeFonts(links);
    if (current && current.version === version) return;
    const css = cssForThemes(themes);
    paintThemes(css);
    keep({ version, css, format: FORMAT, links, dark: themes.filter((t) => t.dark && SLUG.test(t.slug)).map((t) => t.slug) });
  } catch {
    // No connection: the kept themes, or the stylesheet's fallback, stay on the page.
  }
}
